import RootStore from '.';
import BaseStore from './BaseStore';
import { action, flow, makeObservable, observable } from 'mobx';
import { ResponseData } from '@/types/http';
import { PaymentHistoryItem, ReservationDetailItem, ReservationForUpdate, ReservationListItem, ReservationReportHistoryResponse, ReservationReturnHistoryResponse, TransactionHistoryItem, TransactionReservationInfor } from '@/types/reservation';
import { PaginationState, TableState } from '@tanstack/react-table';
import { ReservationApi } from '@/apis';
import { CancelReservationRequest, GetReportBikeDetailResponse, PostReportBikeRequest, RequestCancelReservationRequest, UpdateReservationRequest } from '@/types/http-payload/reservation';

export default class ReservationStore extends BaseStore {
    reservationCMSList: ReservationListItem[] = [];
    reservationENDList: ReservationListItem[] = [];
    reservation: ReservationDetailItem = {} as ReservationDetailItem;
    reservationEND: ReservationDetailItem = {} as ReservationDetailItem;
    transactions: TransactionHistoryItem[] = [];
    reservationTransactionInfor: TransactionReservationInfor = {} as TransactionReservationInfor;
    reservationForUpdate: ReservationForUpdate = {} as ReservationForUpdate;
    totalPage: number = 0;

    // report, return bike
    reportBikeDetail: GetReportBikeDetailResponse = {} as GetReportBikeDetailResponse;
    reservationsReportHistory: ReservationReportHistoryResponse = {} as ReservationReportHistoryResponse;
    reservationsReturnHistory: ReservationReturnHistoryResponse = {} as ReservationReturnHistoryResponse;

    // payment history
    paymentHistory: PaymentHistoryItem[] = [];

    totalTransactionRecord: number = 0;
    totalTransactionPage: number = 0;
    defautlTransactionPaging: TableState = {
        pagination: {
            pageIndex: 0,
            pageSize: 5
        } as PaginationState,
        sorting: [{}]
    } as TableState;

    transactionPaging = this.defautlTransactionPaging;

    api: ReservationApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            reservationCMSList: observable,
            reservationENDList: observable,
            reservation: observable,
            reportBikeDetail: observable,
            reservationsReportHistory: observable,
            reservationsReturnHistory: observable,
            reservationEND: observable,
            transactions: observable,
            reservationTransactionInfor: observable,
            reservationForUpdate: observable,
            paymentHistory: observable,
            getReservationCMSList: flow.bound,
            getReservationCMSDetail: flow.bound,
            requestCancelReservation: flow.bound,
            cancelReservation: flow.bound,
            updateReservation: flow.bound,
            getReservationENDList: flow.bound,
            getReservationENDDetail: flow.bound,
            rePayment: flow.bound,
            // returnBike: flow.bound,
            getReportBikeDetail: flow.bound,
            postReportBike: flow.bound,
            getReservationsReportHistory: flow.bound,
            getReservationsReturnHistory: flow.bound,
            getTransactionHistory: flow.bound,
            cancelReservationEND: flow.bound,
            downloadInvoice: flow.bound,
            downloadReceipt: flow.bound,
            confirmDataForUpdateInfor: flow.bound,
            updateReservationInfor: flow.bound,
            getPaymentHistory: flow.bound,
            requestAdditionalPayment: flow.bound,
            cancelAdditionalPayment: flow.bound,
            updateReservationStatus: flow.bound,
            requestUpdateBookingByCustomer: flow.bound,
            cleanDetail: action.bound,
            cleanTransaction: action.bound,
            cleanRental: action.bound
        });
        this.api = new ReservationApi();
    }

    // #region CMS
    *getReservationCMSList(searchParams: any, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationCMSList, payload);
            if (res?.ok) {
                this.reservationCMSList = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
            }
        } catch (error) {
        }
    }

    *getReservationCMSDetail(payload) {
        try {
            const res: ResponseData<ReservationDetailItem> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationCMSDetail, payload);
            if (res?.ok) {
                this.reservation = res.data;
                return res.data;
            }
        } catch (error) {
        }
    }

    *requestCancelReservation(payload: RequestCancelReservationRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.cancelReservationCMS, payload);
            if (res?.ok) {
                return true;
            }
        } catch (error) {
        }
    }

    *cancelReservation(payload: CancelReservationRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.cancelReservationCMS, payload);
            if (res?.ok) {
                return true;
            }
        } catch (error) {
        }
    }

    *updateReservation(payload: UpdateReservationRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateReservationCMS, payload);
            if (res?.ok) {
                return true;
            }
        } catch (error) {
        }
    }

    *confirmDataForUpdateInfor(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.confirmUpdateBooking, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *updateReservationInfor(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateBookingInfor, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *updateReservationStatus(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateReservationStatusCMS, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *requestAdditionalPayment(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.requestAdditionalPayment, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }

    *cancelAdditionalPayment(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.cancelAdditionalPayment, payload);
            if (res?.ok) {
                return res?.data;
            }
        } catch (error) {
        }
    }
    // #endregion

    // #region ENDUSER
    *getReservationENDList(searchParams?: any, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationENDList, payload);
            if (res?.ok) {
                this.reservationENDList = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
                this.totalPage = res.data.paginate.totalPage;
            }
        } catch (error) {
        }
    }

    *getReservationENDDetail(id: number) {
        try {
            const res: ResponseData<ReservationDetailItem> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationENDDetail, id);
            if (res?.ok) {
                this.reservationEND = res.data;
                return res.data;
            }
        } catch (error) {
        }
    }

    *rePayment(id: number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.rePayment, id);
            if (res?.ok) {
                window.location.href = res.data?.linkReferGMOPayment;
            }
        } catch (error) {
        }
    }

    *getTransactionHistory(searchParams, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.transactionPaging), ...searchParams };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getTransactionHistory, payload);
            if (res?.ok) {
                this.transactions = res.data.history.elements;
                this.reservationTransactionInfor = res.data;
                this.transactionPaging = {
                    ...this.transactionPaging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.history.paginate)
                };
                this.totalTransactionRecord = res.data.history.paginate.totalRecord;
            }
        } catch (error) {
        }
    }

    *cancelReservationEND(payload: { id: number, note: string }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.cancelReservationEND, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
        }
    }

    *requestUpdateBookingByCustomer(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.requestUpdateBooking, payload);
            if (res?.ok) {
                return true;
            }
        } catch (error) {
        }
    }
    // #endregion

    // #region Report Bike
    *getReportBikeDetail(payload) {
        try {
            const res: ResponseData<GetReportBikeDetailResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getReportBikeDetail, payload);
            if (res?.ok) {
                this.reportBikeDetail = res.data;
                return res.data;
            }
            return false;
        } catch (error) {
        }
    }

    *postReportBike(id: number | string, payload: PostReportBikeRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.postReportBike, { ...payload, id });
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
        }
    }

    *getReservationsReportHistory(id: string | number) {
        try {
            const res: ResponseData<ReservationReportHistoryResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationsReportHistory, id);
            if (res?.ok) {
                this.reservationsReportHistory = res.data;
                return res.data;
            }
            return false;
        } catch (error) {
        }
    }

    *getReservationsReturnHistory(id: string | number) {
        try {
            const res: ResponseData<ReservationReturnHistoryResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getReservationsReturnHistory, id);
            if (res?.ok) {
                this.reservationsReturnHistory = res.data;
                return res.data;
            }
            return false;
        } catch (error) {
        }
    }

    // #endregion Report Bike

    // #region download file
    *downloadInvoice(payload) {
        try {
            const res: any = yield this.rootStore.apiStore.call(this.api, this.api.downloadFileInvocie, { ...payload });
            return res;
        } catch (error) {
            return false;
        }
    }

    *downloadReceipt(payload) {
        try {
            const res: any = yield this.rootStore.apiStore.call(this.api, this.api.downloadFileReceipt, { ...payload });
            return res;
        } catch (error) {
            return false;
        }
    }
    // #endregion

    *getPaymentHistory(payload: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getAllPaymentHistoryByOrder, payload);
            if (res?.ok) {
                this.paymentHistory = res?.data || [];
                return res?.data;
            }
        } catch (error) {
        }
    }

    clean(): void {
        super.clean();
        this.reservationCMSList = [];
        this.reservationENDList = [];
        this.reservation = {} as ReservationDetailItem;
        this.reservationEND = {} as ReservationDetailItem;

        this.reportBikeDetail = {} as GetReportBikeDetailResponse;
        this.reservationsReportHistory = {} as ReservationReportHistoryResponse;
        this.totalPage = 0;
        this.reservationsReturnHistory = {} as ReservationReturnHistoryResponse;
        this.paymentHistory = [];
    }

    cleanDetail(): void {
        this.reservation = {} as ReservationDetailItem;
        this.reservationEND = {} as ReservationDetailItem;
        this.paymentHistory = [];
    }

    cleanTransaction(): void {
        this.transactions = [];
        this.reservationTransactionInfor = {} as TransactionReservationInfor;
        this.transactionPaging = this.defautlTransactionPaging;
        this.totalTransactionRecord = 0;
    }

    cleanRental(): void {
        super.clean();
        this.reservationENDList = [];
        this.totalPage = 0;
        this.keepSearchParams = {};
    }
}
